// Pure segment algebra for VAD annotation.
//
// A "segments" value is an array of { start, end, labels[] } that ALWAYS tiles
// the clip edge-to-edge: segs[0].start === 0, segs[N].end === duration, and
// segs[i].end === segs[i+1].start. Every operation preserves that invariant by
// construction, so the UI never needs coverage math or boundary-sync callbacks.

export const LABELS = ["kid", "adult", "doro", "noise", "silence", "notsure"];
export const EXCLUSIVE_LABELS = ["silence", "notsure"];
const DEFAULT_MIN_WIDTH = 0.005;  // 5ms — allow very small segments (backend floor ~1ms)

export function initialSegments(duration) {
  return [{ start: 0, end: duration, labels: [] }];
}

// Insert a boundary at time t, splitting whichever segment contains it into two
// adjacent segments that each copy the parent's labels. No-op if t is out of
// range or either resulting piece would be narrower than minWidth.
export function splitSegments(segments, t, minWidth = DEFAULT_MIN_WIDTH) {
  const idx = segments.findIndex((s) => t > s.start && t < s.end);
  if (idx === -1) return segments;
  const seg = segments[idx];
  if (t - seg.start < minWidth || seg.end - t < minWidth) return segments;
  const left = { start: seg.start, end: t, labels: [...seg.labels] };
  const right = { start: t, end: seg.end, labels: [...seg.labels] };
  return [...segments.slice(0, idx), left, right, ...segments.slice(idx + 1)];
}

// Merge segment i with i+1 (i.e. delete the boundary between them), unioning
// their labels. No-op for the last segment.
export function mergeSegments(segments, i) {
  if (i < 0 || i >= segments.length - 1) return segments;
  const a = segments[i];
  const b = segments[i + 1];
  let labels = [...new Set([...a.labels, ...b.labels])];
  const exclusive = labels.filter(l => EXCLUSIVE_LABELS.includes(l));
  const nonExclusive = labels.filter(l => !EXCLUSIVE_LABELS.includes(l));
  if (exclusive.length > 1 || (exclusive.length === 1 && nonExclusive.length > 0)) {
    labels = exclusive.includes("notsure") ? [] : nonExclusive;
  }
  const merged = { start: a.start, end: b.end, labels };
  return [...segments.slice(0, i), merged, ...segments.slice(i + 2)];
}

// Move the shared edge between segment b and b+1 to time t, clamped so neither
// side becomes narrower than minWidth. Labels on each side are preserved.
export function moveBoundary(segments, b, t, minWidth = DEFAULT_MIN_WIDTH) {
  if (b < 0 || b >= segments.length - 1) return segments;
  const left = segments[b];
  const right = segments[b + 1];
  const lo = left.start + minWidth;
  const hi = right.end - minWidth;
  const clamped = Math.max(lo, Math.min(hi, t));
  return segments.map((s, j) => {
    if (j === b) return { ...s, end: clamped };
    if (j === b + 1) return { ...s, start: clamped };
    return s;
  });
}

// Toggle a label on segment i, returning a new array (no mutation).
export function toggleLabel(segments, i, label) {
  return segments.map((s, j) => {
    if (j !== i) return s;
    const has = s.labels.includes(label);
    return {
      ...s,
      labels: has ? s.labels.filter((l) => l !== label) : [...s.labels, label],
    };
  });
}

export function allLabeled(segments) {
  return segments.every((s) => s.labels.length > 0);
}

// Serialize to the submit/export shape: one object per segment, labels together.
export function toAnnotations(segments, precision = 6) {
  const round = (n) => parseFloat(n.toFixed(precision));
  return segments.map((s) => ({
    start: round(s.start),
    end: round(s.end),
    labels: [...s.labels],
  }));
}
